import { useDebounce } from '@uidotdev/usehooks';
import EmptyState from 'components/EmptyState';
import { ModalHeader } from 'components/modals/ModalHeader';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FieldLabel } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { useEffect, useMemo, useState } from 'react';
import { useLazySearchEinvoiceAddressesQuery } from 'store/api/einvoiceAddresses';
import type { EinvoiceAddress } from 'types/einvoiceAddress';
import { t } from 'utils/getTranslations';
import { getEinvoiceOperatorName } from './einvoiceOperatorNames';

const SEARCH_MIN_LENGTH = 3;
const SEARCH_INPUT_ID = 'einvoice-address-picker-search';
const PAGE_SIZE = 20;

// 86% of registry rows repeat the company name inside addressName; keep only what the site name adds.
function siteSuffix({ companyName, addressName }: EinvoiceAddress) {
    if (!addressName) {
        return '';
    }
    const lowerAddress = addressName.toLowerCase();
    const lowerCompany = companyName.toLowerCase();
    if (lowerAddress === lowerCompany || lowerCompany.startsWith(lowerAddress)) {
        return '';
    }
    if (!lowerAddress.startsWith(lowerCompany)) {
        return ` — ${addressName}`;
    }
    const rest = addressName.slice(companyName.length).replace(/^[\s—–-]+/, '');
    if (!rest) {
        return '';
    }
    return rest.startsWith('(') ? ` ${rest}` : ` — ${rest}`;
}

export type EinvoiceAddressPickerModalProps = {
    onClose: () => void;
    onSelect: (row: EinvoiceAddress) => void;
    initialSearchValue?: string;
};

export function EinvoiceAddressPickerModal({
    onClose,
    onSelect,
    initialSearchValue = '',
}: EinvoiceAddressPickerModalProps) {
    // TODO(react-doctor: no-derived-useState): seeds the initial value only; intentional.
    const [searchValue, setSearchValue] = useState(initialSearchValue);
    const debouncedSearch = useDebounce(searchValue, 350);
    const trimmedSearch = debouncedSearch.trim();
    const meetsMinLength = trimmedSearch.length >= SEARCH_MIN_LENGTH;

    const columns = useMemo<DataTableColumnDef<EinvoiceAddress>[]>(
        () => [
            {
                header: t('einvoiceAddress.picker.column.company'),
                accessorKey: 'companyName',
                enableSorting: false,
                meta: { widthWeight: 2 },
                cell: ({ row }) => `${row.original.companyName}${siteSuffix(row.original)}`,
            },
            {
                header: t('einvoiceAddress.picker.column.business-id'),
                accessorKey: 'businessId',
                enableSorting: false,
                meta: { widthWeight: 1 },
            },
            {
                header: t('einvoiceAddress.picker.column.vat-number'),
                accessorKey: 'vatNumber',
                enableSorting: false,
                meta: { widthWeight: 1 },
            },
            {
                header: t('einvoiceAddress.picker.column.address'),
                accessorKey: 'einvoiceAddress',
                enableSorting: false,
                meta: { widthWeight: 1 },
            },
            {
                header: t('einvoiceAddress.picker.column.operator'),
                accessorKey: 'operatorCode',
                enableSorting: false,
                meta: { widthWeight: 1 },
                cell: ({ row }) => getEinvoiceOperatorName(row.original.operatorCode),
            },
        ],
        []
    );

    const [fetchAddresses, { data, isLoading, isFetching, isError }, { lastArg }] =
        useLazySearchEinvoiceAddressesQuery();
    const isSearching = isLoading || isFetching;

    // TODO(react-doctor: no-effect-event-handler): debounced value, not the input event; intentional.
    useEffect(() => {
        if (meetsMinLength) {
            fetchAddresses({ page: 0, size: PAGE_SIZE, search: trimmedSearch });
        }
    }, [meetsMinLength, trimmedSearch, fetchAddresses]);

    const retrySearch = () => fetchAddresses({ page: 0, size: PAGE_SIZE, search: trimmedSearch });

    // The loading flags cannot tell "never fetched" or "term moved on" from a settled empty result.
    const hasFreshData = meetsMinLength && !isSearching && !isError && lastArg?.search === trimmedSearch;
    const results = hasFreshData ? (data?.content ?? []) : [];
    const totalElements = data?.totalElements ?? 0;
    const isTruncated = hasFreshData && totalElements > results.length;
    const totalCountAccurate = data?.totalCountAccurate ?? true;

    return (
        <>
            <ModalHeader title={t('einvoiceAddress.picker.title')} onCancel={onClose} />
            <div className="overflow-y-auto p-5">
                <div className="mb-4">
                    <FieldLabel className="text-fg-secondary mb-1 font-normal" htmlFor={SEARCH_INPUT_ID}>
                        {t('einvoiceAddress.picker.search-label')}
                    </FieldLabel>
                    <Input
                        id={SEARCH_INPUT_ID}
                        className="bg-paper"
                        autoFocus
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        placeholder={t('einvoiceAddress.picker.search-label')}
                    />
                </div>
                <div aria-live="polite">
                    {!meetsMinLength ? (
                        <EmptyState size="inline" titleId="einvoiceAddress.picker.min-length" />
                    ) : (
                        <>
                            {isTruncated && (
                                <p className="text-body2 text-gray-500">
                                    {t(
                                        totalCountAccurate
                                            ? 'einvoiceAddress.picker.truncated'
                                            : 'einvoiceAddress.picker.truncated-approx',
                                        {
                                            shown: String(results.length),
                                            total: String(totalElements),
                                        }
                                    )}
                                </p>
                            )}
                            {!isSearching && isError ? (
                                <EmptyState
                                    size="inline"
                                    variant="error"
                                    onRetry={retrySearch}
                                    surface="einvoice-address-picker"
                                />
                            ) : (
                                hasFreshData && (
                                    <DataTable
                                        data={results}
                                        columns={columns}
                                        getRowId={(row) => row.id}
                                        onRowClick={onSelect}
                                        emptyState={
                                            <EmptyState
                                                size="inline"
                                                titleId="einvoiceAddress.picker.no-matches"
                                                titleValues={{ search: trimmedSearch }}
                                                surface="einvoice-address-picker"
                                            />
                                        }
                                    />
                                )
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
